// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('authUser');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // null = unchecked, true = Kite configured, false = not configured
  const [kiteReady, setKiteReady] = useState(() => {
    const s = localStorage.getItem('kiteReady');
    if (s === 'true') return true;
    if (s === 'false') return false;
    return null;
  });

  const checkKiteStatus = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const res = await fetch('/api/user/token/current', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setKiteReady(false);
        localStorage.setItem('kiteReady', 'false');
        return;
      }
      const d = await res.json();
      const ready = !!d.has_access_token;
      setKiteReady(ready);
      localStorage.setItem('kiteReady', String(ready));
    } catch {
      // silently fail — don't block the app
    }
  }, []);

  // Auto-check Kite status on mount if user is logged in and status is unknown
  useEffect(() => {
    if (user && kiteReady === null) {
      checkKiteStatus();
    }
  }, [user, kiteReady, checkKiteStatus]);

  const login = useCallback((token, userData) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('authUser', JSON.stringify(userData));
    // Clear cached Kite status so it gets re-checked for the new session
    localStorage.removeItem('kiteReady');
    setKiteReady(null);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('kiteReady');
    localStorage.removeItem('kiteBannerDismissed');
    setKiteReady(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user, kiteReady, checkKiteStatus }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
